import { DatePicker, Divider, Form, InputNumber, Select, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getInstructors,
  selectAssignableInstructors,
} from "store/slices/instructorsSlice";
import { openModal } from "store/slices/modalsSlice";
import {
  getStudentFormationsForFormation,
  getTrainingById,
  getTrainings,
} from "store/slices/trainingsSlice";

const { Option } = Select;
const { Text } = Typography;

const TrainingGroupForm = ({
  form,
  onSubmit,
  schoolId,
  formationId: initialFormationId,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Redux selectors
  const { availableStudentFormations, training, trainings } = useSelector(
    (state) => state?.trainings
  );
  const instructors = useSelector(selectAssignableInstructors);
  const [studentsAvailable, setStudentsAvailable] = useState([]);
  // Local state
  const [selectedFormationId, setSelectedFormationId] = useState(
    initialFormationId || null
  );
  const [selectedTrainingState, setSelectedTrainingState] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [formationInstructors, setFormationInstructors] = useState([]);
  const groupTypes = ["online", "onsite"];
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  // Whenever the user picks a formation, load everything for it
  useEffect(() => {
    if (!selectedFormationId) return;
    // 1) fetch instructors
    dispatch(getInstructors());
    // 2) fetch training details
    dispatch(getTrainingById(selectedFormationId));
    // 3) fetch available students
    dispatch(
      getStudentFormationsForFormation({
        trainingId: selectedFormationId,
        groupId: null,
      })
    );
    // 4) populate hidden form fields
    form?.setFieldsValue({
      schoolId,
      formationId: selectedFormationId,
    });
  }, [dispatch, selectedFormationId, form, schoolId]);
  useEffect(() => {
    if (!availableStudentFormations) {
      setStudentsAvailable([]);
      return;
    }

    if (!selectedTrainingState) {
      setStudentsAvailable(availableStudentFormations);
      return;
    }

    const filtered = availableStudentFormations.filter((stu) => {
      if (stu?.learningType === "subscription") {
        return true;
      }
      return stu?.learningType === selectedTrainingState;
    });
    setStudentsAvailable(filtered);
  }, [availableStudentFormations, selectedTrainingState]);
  useEffect(() => {

    if (Array?.isArray(training?.instructors)) {
      setFormationInstructors(
        training?.instructors?.map((inst) =>
          typeof inst === "object" ? inst?._id : inst
        )
      );
    }
  }, [training, form]);

  // Student‐count validator
  const validateStudentsCount = (_, value) => {
    if (!value || !selectedTrainingState) return Promise?.resolve();
    const count = value?.length;
    switch (selectedTrainingState) {
      case "particulier":
        if (count !== 1)
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.single")
          );
        break;
      case "miniGroupe":
        // temp
        if (count < 2 || count > 3)
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.mini")
          );
        break;
      case "groupe":
        if (count < 4)
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.group")
          );
        break;
      default:
        break;
    }
    return Promise?.resolve();
  };

  // Filter down to the 3 allowed states
  const getTrainingStates = () =>
    training?.state?.filter((s) =>
      ["particulier", "miniGroupe", "groupe"]?.includes(s)
    ) || [];

  // Put formation’s own instructors at top of the list
  const getSortedInstructors = () => {
    console?.log("instructors:", instructors);

    if (!instructors) return [];
    return [...instructors]?.sort((a, b) => {
      const aIs = formationInstructors?.includes(a?._id);
      const bIs = formationInstructors?.includes(b?._id);
      if (aIs && !bIs) return -1;
      if (!aIs && bIs) return 1;
      return 0;
    });
  };

  // Check if an instructor should be highlighted
  const isFormationInstructor = (id) =>
    Array?.isArray(training?.instructors) &&
    training?.instructors?.some(
      (inst) =>
        (inst?._id === id ||
          inst?.instructorId === id ||
          inst?.instructorId?._id === id) &&
        inst?.isApproved === "approved"
    );

  const renderStudentOptionLabel = (student) => {
    if (!student) return "";
    const learningTypeLabel = student?.learningType
      ? t(`groups.learningType.${student.learningType}`)
      : "";

    if (student?.learningType === "subscription") {
      return `${student?.studentName} – ${
        t("groups.subscription.studentFromPack") || learningTypeLabel
      }`;
    }

    const currency = student?.payment?.currency;
    if (!currency) {
      return `${student?.studentName}${
        learningTypeLabel ? ` – ${learningTypeLabel}` : ""
      }`;
    }

    const paidAmount =
      typeof student?.payment?.amount === "number"
        ? student.payment.amount
        : 0;
    const totalAmount =
      typeof student?.price?.[currency] === "number"
        ? student.price[currency]
        : "-";
    const displayCurrency = currency ? currency.toUpperCase() : "";

    return `${student?.studentName} : ${paidAmount} / ${totalAmount} (${displayCurrency})${
      learningTypeLabel ? ` – ${learningTypeLabel}` : ""
    }`;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) =>
        onSubmit({
          ...values,
          schoolId,
          formationId: values?.formationId,
          groupState: selectedTrainingState,
          groupeType: values?.groupeType,
          students: values?.students?.map((sid) => ({ studentFormationId: sid })),
          instructorIds: values?.instructorIds,
        })
      }
    >
      {/* 1) Formation selector */}
      <Form.Item
        name="formationId"
        label={t("groups.crud.form.label.formation")}
        rules={[
          {
            required: true,
            message: t("groups.crud.form.error.required.formation"),
          },
        ]}
      >
        <Select
          placeholder={t("groups.crud.form.placeholder.formation")}
          value={selectedFormationId}
          onChange={(val) => {
            setSelectedFormationId(val);
            dispatch(
              openModal({
                modalType: "new_training_group",
                modalProps: {
                  schoolId: modalProps?.schoolId || schoolId,
                  formationId: val,
                },
              })
            );
            // reset downstream fields
            form?.resetFields([
              "groupeType",
              "groupState",
              "students",
              "instructorIds",
              "paymentMode",
              "paymentValue",
            ]);
            setSelectedType(null);
            setSelectedTrainingState(null);
          }}
        >
          {trainings?.map((tr) => (
            <Option key={tr?._id} value={tr?._id}>
              {tr?.name}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {/* 2) Group type */}
      <Form.Item
        name="groupeType"
        label={t("groups.crud.form.label.type")}
        rules={[
          {
            required: true,
            message: t("groups.crud.form.error.required.type"),
          },
        ]}
      >
        <Select
          placeholder={t("groups.crud.form.placeholder.type")}
          disabled={!selectedFormationId}
          onChange={(val) => {
            setSelectedType(val);
            form?.resetFields(["groupState", "students"]);
            setSelectedTrainingState(null);
          }}
        >
          {groupTypes?.map((type) => (
            <Option key={type} value={type}>
              {t(`groups.type.${type}`)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {/* 3) Training state */}
      <Form.Item
        name="groupState"
        label={t("groups.crud.form.label.trainingState")}
        rules={[
          {
            required: true,
            message: t("groups.crud.form.error.required.trainingState"),
          },
        ]}
      >
        <Select
          disabled={!selectedFormationId}
          onChange={(val) => {
            setSelectedTrainingState(val);
            form?.resetFields(["students"]);
          }}
        >
          {getTrainingStates()?.map((state) => (
            <Option key={state} value={state}>
              {t(`trainings.state.${state?.replace(/\s+/g, "")?.toLowerCase()}`)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {/* 4) Students multi‑select */}
      {selectedTrainingState && (
        <Form.Item
          name="students"
          label={t("groups.crud.form.label.students")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.students"),
            },
            { validator: validateStudentsCount },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={t("groups.crud.form.placeholder.students")}
          >
            {studentsAvailable?.map((stu) => (
              <Option
                key={stu?.studentFormationId}
                value={stu?.studentFormationId}
              >
                {renderStudentOptionLabel(stu)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {/* 5) Instructors multi-selector */}
      <Form.Item
        name="instructorIds"
        label={t("groups.crud.form.label.instructors")}
        rules={[
          {
            required: true,
            message: t("groups.crud.form.error.required.instructors"),
          },
        ]}
      >
        <Select
          mode="multiple"
          placeholder={t("groups.crud.form.placeholder.instructors")}
          optionLabelProp="label"
          onChange={(val) => {
            dispatch(
              openModal({
                modalType: "new_training_group",
                modalProps: {
                  schoolId: modalProps?.schoolId || schoolId,
                  formationId: selectedFormationId,
                },
              })
            );
          }}
        >
          {getSortedInstructors()?.map((inst) => (
            <Option key={inst?._id} value={inst?._id} label={inst?.fullName}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text
                  strong={isFormationInstructor(inst?._id)}
                  style={{
                    color: isFormationInstructor(inst?._id)
                      ? "#1890ff"
                      : undefined,
                  }}
                >
                  {inst?.fullName}
                </Text>
                {isFormationInstructor(inst?._id) && (
                  <Tag color="blue">
                    {t("groups.crud.form.label.formationInstructor")}
                  </Tag>
                )}
              </div>
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="paymentMode"
        label={t("groups.crud.form.label.paymentMode")}
        rules={[
          {
            required: true,
            message: t("groups.crud.form.error.required.paymentMode"),
          },
        ]}
      >
        <Select
          placeholder={t("groups.crud.form.placeholder.paymentMode")}
          onChange={(val) => {
            if (val === "percentage") {
              form?.setFieldsValue({ paymentValue: 40 });
            } else {
              form?.resetFields(["paymentValue"]);
            }
          }}
          options={[
            {
              label: t("groups.crud.form.option.paymentMode.fixedPerHour"),
              value: "fixedPerHour",
            },
            {
              label: t("groups.crud.form.option.paymentMode.percentage"),
              value: "percentage",
            },
          ]}
        />
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.paymentMode !== curr.paymentMode}>
        {({ getFieldValue }) => {
          const paymentMode = getFieldValue("paymentMode");

          if (paymentMode === "fixedPerHour") {
            return (
              <Form.Item
                name="paymentValue"
                label={t("groups.crud.form.label.paymentValue.fixedPerHour")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "groups.crud.form.error.required.paymentValue.fixedPerHour"
                    ),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  precision={2}
                  placeholder={t(
                    "groups.crud.form.placeholder.paymentValue.fixedPerHour"
                  )}
                />
              </Form.Item>
            );
          }

          if (paymentMode === "percentage") {
            return (
              <Form.Item
                name="paymentValue"
                label={t("groups.crud.form.label.paymentValue.percentage")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "groups.crud.form.error.required.paymentValue.percentage"
                    ),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  precision={2}
                  addonAfter="%"
                  placeholder={t(
                    "groups.crud.form.placeholder.paymentValue.percentage"
                  )}
                />
              </Form.Item>
            );
          }

          return null;
        }}
      </Form.Item>
      {/* 6) Starting date */}
      <Form.Item
        name="startingDate"
        label={t("groups.crud.form.label.startDate")}
        rules={[
          {
            required: true,
            message: t("groups.crud.form.error.required.startDate"),
          },
        ]}
      >
        <DatePicker style={{ width: "100%" }} />
      </Form.Item>
    </Form>
  );
};

export default TrainingGroupForm;
